<script setup lang="ts">
import { computed, onBeforeUnmount, ref, watch } from 'vue'
import PreviewGuideOverlay from '@/components/workspace/PreviewGuideOverlay.vue'
import { getCachedImageResource } from '@/features/preview/imageResourceCache'
import {
  buildPreviewImageCorners,
  clampPreviewPoint,
  getPreviewImagePivot,
  isPointInsidePreviewQuad,
  normalizeDegrees,
  scalePreviewQuad,
  toDesignPoint,
  toScreenPoint,
  type PreviewImageGeometry,
  type PreviewPoint,
  type PreviewQuad,
} from '@/features/preview/imageGeometry'
import {
  PREVIEW_MOVE_SNAP_STEP,
  PREVIEW_ROTATION_SNAP_STEP,
  PREVIEW_SIZE_SNAP_STEP,
  snapPreviewValue,
  type PreviewGuideCoordinate,
} from '@/features/preview/previewGuide'
import {
  buildEditablePreviewTarget,
  type PreviewEditableImageChoiceTarget,
  type PreviewEditableImageTarget,
} from '@/features/preview/previewSceneBuilder'
import type CustomUIData from '@/model/CustomUI/CustomUIData'
import CustomUIItem from '@/model/CustomUI/CustomUIItem'
import type EventBase from '@/model/EventClass/EventBase'
import EventButtonDif from '@/model/EventClass/EventButtonDif'
import EventID from '@/model/EventClass/EventID'
import EventMovePic from '@/model/EventClass/EventMovePic'
import EventPicShow from '@/model/EventClass/EventPicShow'
import EventRotatePic from '@/model/EventClass/EventRotatePic'
import type MainProject from '@/model/MainProject'
import { useSceneEditorStore } from '@/store/sceneEditor'

const props = defineProps<{
  events: EventBase[]
  selectedIndex: number | null | undefined
  revision: number
  project: MainProject | null
  stageWidth: number
  stageHeight: number
  previewScale: number
  customUiData?: CustomUIData | null
  customUiRevision?: number
  resolveGraphicResourceUrl: (relativePath: string) => string
  resolveUiGraphicResourceUrl?: (storedPath: string) => string
  resolveOtherGraphicResourceUrl?: (storedPath: string) => string
  resolveImageOptionGraphicResourceUrl: (storedPath: string) => string
}>()

type ImageSizeRecord = {
  width: number
  height: number
}

type ImageDraftPatch = Partial<
  Pick<
    PreviewEditableImageTarget['image'],
    'x' | 'y' | 'zoomX' | 'zoomY' | 'rotation' | 'rotationPivot' | 'rotationPoint'
  >
>

type TransformCapabilities = {
  canMove: boolean
  canScale: boolean
  canRotate: boolean
  canEditPivot: boolean
  showPivot: boolean
}

type CanvasTransformAdapter = {
  id: string
  kind: 'image' | 'image-choice' | 'custom-ui-control'
  geometry: PreviewImageGeometry
  capabilities: TransformCapabilities
  onSelect?: () => void
  applyMove: (nextX: number, nextY: number) => void
  applyScale: (nextZoomX: number, nextZoomY: number) => void
  applyRotate: (deltaY: number, startRotation: number) => void
  applyPivot: (screenPoint: PreviewPoint, geometry: PreviewImageGeometry) => void
}

type RenderTransformModel = {
  adapter: CanvasTransformAdapter
  quad: PreviewQuad
  centerScreen: PreviewPoint
  pivotScreen: PreviewPoint
}

type DragState =
  | null
  | {
      pointerId: number
      adapterId: string
      mode: 'move'
      startPointer: PreviewPoint
      startX: number
      startY: number
    }
  | {
      pointerId: number
      adapterId: string
      mode: 'scale'
      startZoomX: number
      startZoomY: number
      startPointer: PreviewPoint
      startWidth: number
      startHeight: number
    }
  | {
      pointerId: number
      adapterId: string
      mode: 'rotate'
      startPointerY: number
      startRotation: number
    }
  | {
      pointerId: number
      adapterId: string
      mode: 'pivot'
    }

const editor = useSceneEditorStore()
const hostRef = ref<HTMLDivElement | null>(null)
const rootRef = ref<SVGSVGElement | null>(null)
const dragState = ref<DragState>(null)
const activeAdapterId = ref<string | null>(null)
const imageSizeMap = ref<Record<string, ImageSizeRecord>>({})
const draftImagePatch = ref<ImageDraftPatch | null>(null)
const draftChoicePositionMap = ref<Record<number, { x: number; y: number }> | null>(null)
const draftCustomUiPositionMap = ref<Record<number, { x: number; y: number }> | null>(null)
const guideCoordinate = ref<PreviewGuideCoordinate | null>(null)
const guideLabel = ref('')
let pendingFrameHandle = 0
let pendingFrameCommit: (() => void) | null = null
let hasPendingImageEventChange = false

const editableTarget = computed(() => {
  void props.revision
  return buildEditablePreviewTarget(props.events, props.selectedIndex, props.revision)
})

function notifyValueChanged() {
  const maybeEditor = editor as typeof editor & {
    notifySceneEventValueChanged?: () => void
  }
  if (typeof maybeEditor.notifySceneEventValueChanged === 'function') {
    maybeEditor.notifySceneEventValueChanged()
    return
  }
  editor.touchSceneEvents()
}

function buildImageOptionUrl(buttonIndex: number): string {
  const button = props.project?.systemData?.btnList?.buttons?.[buttonIndex] as any
  const storedPath = String(button?.NoneImage?.name ?? button?.MoveImage?.name ?? '').trim()
  if (!storedPath) return ''
  return props.resolveImageOptionGraphicResourceUrl(storedPath)
}

function buildOtherGraphicUrl(path: string): string {
  return props.resolveOtherGraphicResourceUrl?.(path) ?? props.resolveGraphicResourceUrl(path)
}

function buildCustomUiControlUrl(item: CustomUIItem): string {
  if (item.Type === CustomUIItem.controlType.Button) {
    return item.IsUserIndex ? 'custom-ui-default-button' : buildImageOptionUrl(Number(item.Index || 0))
  }
  if (item.Type === CustomUIItem.controlType.Pic) {
    return !item.IsUserString && item.ImagePath1 ? buildOtherGraphicUrl(item.ImagePath1) : ''
  }
  if (item.Type === CustomUIItem.controlType.Drap) {
    return item.ImagePath1 ? buildOtherGraphicUrl(item.ImagePath1) : item.ImagePath2 ? buildOtherGraphicUrl(item.ImagePath2) : ''
  }
  return ''
}

async function ensureImageSizes(urls: string[]) {
  const unique = [...new Set(urls.map((item) => String(item || '').trim()).filter(Boolean))]
  const missing = unique.filter((url) => !imageSizeMap.value[url])
  if (!missing.length) return
  const loaded = await Promise.all(
    missing.map(async (url) => {
      const image = await getCachedImageResource(url)
      return {
        url,
        width: image.naturalWidth || image.width || 0,
        height: image.naturalHeight || image.height || 0,
      }
    }),
  ).catch(() => [])
  if (!loaded.length) return
  imageSizeMap.value = {
    ...imageSizeMap.value,
    ...Object.fromEntries(
      loaded
        .filter((item) => item.width > 0 && item.height > 0)
        .map((item) => [item.url, { width: item.width, height: item.height }]),
    ),
  }
}

watch(
  () => [editableTarget.value, props.revision, props.customUiData, props.customUiRevision] as const,
  ([target]) => {
    const customUiUrls = (props.customUiData?.ControlList ?? [])
      .map((item) => buildCustomUiControlUrl(item as CustomUIItem))
      .filter((url) => url !== 'custom-ui-default-button')
      .filter(Boolean)
    if (customUiUrls.length) {
      void ensureImageSizes(customUiUrls)
    }
    if (target.kind === 'image') {
      const url = props.resolveGraphicResourceUrl(target.image.path)
      void ensureImageSizes([url])
      return
    }
    if (target.kind === 'image-choices') {
      void ensureImageSizes(target.items.map((item) => buildImageOptionUrl(item.buttonIndex)))
    }
  },
  { immediate: true },
)

function scheduleFrameCommit(commit: () => void) {
  pendingFrameCommit = commit
  if (pendingFrameHandle) return
  pendingFrameHandle = requestAnimationFrame(() => {
    pendingFrameHandle = 0
    const job = pendingFrameCommit
    pendingFrameCommit = null
    job?.()
  })
}

function flushPendingCommit() {
  if (pendingFrameHandle) {
    cancelAnimationFrame(pendingFrameHandle)
    pendingFrameHandle = 0
  }
  const job = pendingFrameCommit
  pendingFrameCommit = null
  job?.()
}

function commitPendingImageEventChange() {
  if (!hasPendingImageEventChange) return
  hasPendingImageEventChange = false
  notifyValueChanged()
}

function buildImageAdapter(target: PreviewEditableImageTarget): CanvasTransformAdapter | null {
  const imageState = {
    ...target.image,
    ...(draftImagePatch.value ?? {}),
  }
  const url = props.resolveGraphicResourceUrl(imageState.path)
  const size = imageSizeMap.value[url]
  if (!url || !size) return null
  const geometry: PreviewImageGeometry = {
    x: imageState.x,
    y: imageState.y,
    width: size.width * (imageState.zoomX / 100),
    height: size.height * (imageState.zoomY / 100),
    rotation: imageState.rotation,
    rotationPivot: imageState.rotationPivot,
    rotationPoint: imageState.rotationPoint,
    isMirror: imageState.isMirror,
  }

  const writeValueChange = (patch: ImageDraftPatch) => {
    draftImagePatch.value = {
      ...(draftImagePatch.value ?? {}),
      ...patch,
    }
    hasPendingImageEventChange = true
  }

  const move = (nextX: number, nextY: number) => {
    const event = props.events[target.sourceEventIndex]
    if (!event) return
    const x = Math.round(nextX)
    const y = Math.round(nextY)
    if (event.Code === EventID.PicShow) {
      const picEvent = event as EventPicShow
      picEvent.X = x
      picEvent.Y = y
      writeValueChange({ x, y })
      return
    }
    if (event.Code === EventID.MovePic) {
      const moveEvent = event as EventMovePic
      if (moveEvent.IsAssigned) return
      moveEvent.X = x
      moveEvent.Y = y
      writeValueChange({ x, y })
    }
  }

  const scale = (nextZoomX: number, nextZoomY: number) => {
    const event = props.events[target.sourceEventIndex]
    if (!event) return
    const zoomX = Number(Math.max(0.01, nextZoomX).toFixed(2))
    const zoomY = Number(Math.max(0.01, nextZoomY).toFixed(2))
    if (event.Code === EventID.PicShow) {
      const picEvent = event as EventPicShow
      picEvent.ZoomX = zoomX
      picEvent.ZoomY = zoomY
      writeValueChange({ zoomX, zoomY })
      return
    }
    if (event.Code === EventID.MovePic) {
      const moveEvent = event as EventMovePic
      moveEvent.ZoomX = zoomX
      moveEvent.ZoomY = zoomY
      writeValueChange({ zoomX, zoomY })
    }
  }

  const rotate = (deltaY: number, startRotation: number) => {
    const event = props.events[target.sourceEventIndex] as EventRotatePic | undefined
    if (!event || event.RationAngleValueOrNot !== 0) return
    const nextRotation = Number(snapRotationValue(startRotation - deltaY * 0.4).toFixed(2))
    event.RationAngle = nextRotation
    writeValueChange({ rotation: nextRotation })
  }

  const pivot = (screenPoint: PreviewPoint, currentGeometry: PreviewImageGeometry) => {
    const event = props.events[target.sourceEventIndex] as EventRotatePic | undefined
    if (!event) return
    const designPoint = toDesignPoint(screenPoint, props.previewScale)
    const currentPivot = getPreviewImagePivot(currentGeometry)
    const anchor = {
      x: currentGeometry.x + currentPivot.x,
      y: currentGeometry.y + currentPivot.y,
    }
    const radians = (currentGeometry.rotation * Math.PI) / 180
    const dx = designPoint.x - anchor.x
    const dy = designPoint.y - anchor.y
    const rotatedX = dx * Math.cos(radians) + dy * Math.sin(radians)
    const rotatedY = -dx * Math.sin(radians) + dy * Math.cos(radians)
    const mirroredLocalX = rotatedX + currentPivot.x
    const localX = currentGeometry.isMirror ? currentGeometry.width - mirroredLocalX : mirroredLocalX
    const localY = rotatedY + currentPivot.y
    const clamped = clampPreviewPoint({ x: localX, y: localY }, currentGeometry.width, currentGeometry.height)
    const point = { x: Math.round(clamped.x), y: Math.round(clamped.y) }
    event.PointRate = 10
    event.RotationPoint = `${point.x},${point.y}`
    writeValueChange({
      rotationPivot: 'custom',
      rotationPoint: point,
    })
  }

  return {
    id: `image:${target.sourceEventCode}:${target.sourceEventIndex}`,
    kind: 'image',
    geometry,
    capabilities: {
      canMove: target.capabilities.canMove,
      canScale: target.capabilities.canScale,
      canRotate: target.capabilities.canRotate,
      canEditPivot: target.capabilities.canEditPivot,
      showPivot: target.sourceEventCode === EventID.RotatePic,
    },
    applyMove: move,
    applyScale: scale,
    applyRotate: rotate,
    applyPivot: pivot,
  }
}

function buildChoiceAdapters(target: PreviewEditableImageChoiceTarget): CanvasTransformAdapter[] {
  return target.items
    .map((item) => {
      const draftPosition = draftChoicePositionMap.value?.[item.choiceIndex]
      const x = draftPosition?.x ?? item.x
      const y = draftPosition?.y ?? item.y
      const url = buildImageOptionUrl(item.buttonIndex)
      const size = imageSizeMap.value[url]
      if (!url || !size) return null
      const geometry: PreviewImageGeometry = {
        x,
        y,
        width: size.width,
        height: size.height,
        rotation: 0,
        rotationPivot: 'top-left',
        rotationPoint: { x: 0, y: 0 },
        isMirror: false,
      }
      return {
        id: `choice:${item.choiceIndex}`,
        kind: 'image-choice' as const,
        geometry,
        capabilities: {
          canMove: true,
          canScale: false,
          canRotate: false,
          canEditPivot: false,
          showPivot: false,
        },
        applyMove(nextX: number, nextY: number) {
          const header = props.events[target.headerIndex] as EventButtonDif | undefined
          if (!header) return
          const roundedX = Math.round(nextX)
          const roundedY = Math.round(nextY)
          const nextItems = (header.ButtonChoice ?? [])
            .map((value) => String(value ?? '').split(','))
            .map((parts, index) => {
              const out = [...parts]
              if (index === item.choiceIndex) {
                out[1] = String(roundedX)
                out[2] = String(roundedY)
              }
              return `${String(out[0] ?? index).trim() || String(index)},${String(out[1] ?? '0').trim() || '0'},${String(out[2] ?? '0').trim() || '0'}`
            })
          draftChoicePositionMap.value = {
            ...(draftChoicePositionMap.value ?? {}),
            [item.choiceIndex]: { x: roundedX, y: roundedY },
          }
          scheduleFrameCommit(() => {
            if (editor.syncButtonChoiceBlock(target.headerIndex, nextItems)) {
              activeAdapterId.value = `choice:${item.choiceIndex}`
            }
          })
        },
        applyScale() {},
        applyRotate() {},
        applyPivot() {},
      } satisfies CanvasTransformAdapter
    })
    .filter(Boolean) as CanvasTransformAdapter[]
}

function buildCustomUiControlAdapters(data: CustomUIData | null | undefined): CanvasTransformAdapter[] {
  const items = data?.ControlList ?? []
  return items
    .map((item, index) => {
      const current = item as CustomUIItem
      const draftPosition = draftCustomUiPositionMap.value?.[index]
      const x = draftPosition?.x ?? (current.IsUserVar ? 0 : Number(current.X || 0))
      const y = draftPosition?.y ?? (current.IsUserVar ? 0 : Number(current.Y || 0))
      const url = buildCustomUiControlUrl(current)
      const size = url ? imageSizeMap.value[url] : null
      const width = current.Type === CustomUIItem.controlType.Button && current.IsUserIndex ? 120 : size?.width ?? 160
      const height = current.Type === CustomUIItem.controlType.Button && current.IsUserIndex ? 42 : size?.height ?? 36
      const geometry: PreviewImageGeometry = {
        x,
        y,
        width: Math.max(1, width),
        height: Math.max(1, height),
        rotation: 0,
        rotationPivot: 'top-left',
        rotationPoint: { x: 0, y: 0 },
        isMirror: false,
      }
      return {
        id: `custom-ui:${index}`,
        kind: 'custom-ui-control' as const,
        geometry,
        capabilities: {
          canMove: !current.IsUserVar,
          canScale: false,
          canRotate: false,
          canEditPivot: false,
          showPivot: false,
        },
        onSelect() {
          const uiIndex = editor.selectedCustomUi?.uiIndex ?? 0
          editor.selectCustomUiControl(uiIndex, index)
        },
        applyMove(nextX: number, nextY: number) {
          if (current.IsUserVar) return
          const roundedX = Math.round(nextX)
          const roundedY = Math.round(nextY)
          current.X = roundedX
          current.Y = roundedY
          draftCustomUiPositionMap.value = {
            ...(draftCustomUiPositionMap.value ?? {}),
            [index]: { x: roundedX, y: roundedY },
          }
          scheduleFrameCommit(() => {
            editor.notifyCustomUiChanged()
            activeAdapterId.value = `custom-ui:${index}`
          })
        },
        applyScale() {},
        applyRotate() {},
        applyPivot() {},
      } satisfies CanvasTransformAdapter
    })
}

const transformAdapters = computed<CanvasTransformAdapter[]>(() => {
  void props.revision
  void props.customUiRevision
  if (props.customUiData) {
    return buildCustomUiControlAdapters(props.customUiData)
  }
  const target = editableTarget.value
  if (target.kind === 'image') {
    const adapter = buildImageAdapter(target)
    return adapter ? [adapter] : []
  }
  if (target.kind === 'image-choices') {
    return buildChoiceAdapters(target)
  }
  return []
})

const renderModels = computed<RenderTransformModel[]>(() => {
  const scale = Math.max(0.0001, props.previewScale)
  return transformAdapters.value.map((adapter) => {
    const quad = scalePreviewQuad(buildPreviewImageCorners(adapter.geometry), scale)
    const centerScreen = {
      x: (quad[0].x + quad[1].x + quad[2].x + quad[3].x) / 4,
      y: (quad[0].y + quad[1].y + quad[2].y + quad[3].y) / 4,
    }
    const pivot = getPreviewImagePivot(adapter.geometry)
    const pivotScreen = toScreenPoint(
      {
        x: adapter.geometry.x + pivot.x,
        y: adapter.geometry.y + pivot.y,
      },
      scale,
    )
    return {
      adapter,
      quad,
      centerScreen,
      pivotScreen,
    }
  })
})

const isVisible = computed(() => renderModels.value.length > 0 && props.stageWidth > 0 && props.stageHeight > 0)
const guideVisible = computed(() => {
  const mode = dragState.value?.mode
  return !!guideCoordinate.value || mode === 'move' || mode === 'scale' || mode === 'rotate'
})
const guideStageScale = computed(() => {
  if (props.previewScale > 0) return props.previewScale
  const width = Number(props.project?.screenHash?.width ?? 0)
  return width > 0 ? props.stageWidth / width : 1
})

function getPointerPoint(event: PointerEvent): PreviewPoint {
  const rect = hostRef.value?.getBoundingClientRect()
  if (!rect) return { x: 0, y: 0 }
  return {
    x: event.clientX - rect.left,
    y: event.clientY - rect.top,
  }
}

function getImagePath(model: RenderTransformModel) {
  return model.quad.map((point) => `${point.x},${point.y}`).join(' ')
}

function getRenderModelById(id: string | null | undefined) {
  return renderModels.value.find((item) => item.adapter.id === id) ?? null
}

function hitRenderModel(point: PreviewPoint) {
  for (let index = renderModels.value.length - 1; index >= 0; index -= 1) {
    const model = renderModels.value[index]
    if (isPointInsidePreviewQuad(point, model.quad)) {
      return model
    }
  }
  return null
}

function getCurrentImageZoom() {
  const target = editableTarget.value
  if (target.kind !== 'image') {
    return { zoomX: 100, zoomY: 100 }
  }
  return {
    zoomX: draftImagePatch.value?.zoomX ?? target.image.zoomX,
    zoomY: draftImagePatch.value?.zoomY ?? target.image.zoomY,
  }
}

function getGuideAnchor(model: RenderTransformModel): PreviewGuideCoordinate {
  return {
    x: model.adapter.geometry.x,
    y: model.adapter.geometry.y,
  }
}

function updateScaleGuide(model: RenderTransformModel, width: number, height: number) {
  guideCoordinate.value = getGuideAnchor(model)
  guideLabel.value = `W: ${Math.round(width)}, H: ${Math.round(height)}`
}

function snapMoveValue(value: number) {
  return snapPreviewValue(value, PREVIEW_MOVE_SNAP_STEP)
}

function snapRotationValue(value: number) {
  return normalizeDegrees(snapPreviewValue(value, PREVIEW_ROTATION_SNAP_STEP))
}

function resolveSnappedScale(state: Extract<NonNullable<DragState>, { mode: 'scale' }>, rawRatio: number) {
  const width = Math.max(1, state.startWidth * rawRatio)
  const snappedWidth = Math.max(PREVIEW_SIZE_SNAP_STEP, snapPreviewValue(width, PREVIEW_SIZE_SNAP_STEP))
  const ratio = snappedWidth / Math.max(0.0001, state.startWidth)
  return {
    zoomX: state.startZoomX * ratio,
    zoomY: state.startZoomY * ratio,
    width: snappedWidth,
    height: Math.max(1, state.startHeight * ratio),
  }
}

function formatAngleLabel(value: number) {
  const angle = Number(normalizeDegrees(value).toFixed(1))
  const sign = angle > 0 ? '+' : ''
  return `角度: ${sign}${angle}°`
}

function beginPointerScale(model: RenderTransformModel, pointerId: number, point: PreviewPoint) {
  const currentZoom = getCurrentImageZoom()
  updateScaleGuide(model, model.adapter.geometry.width, model.adapter.geometry.height)
  dragState.value = {
    pointerId,
    adapterId: model.adapter.id,
    mode: 'scale',
    startZoomX: currentZoom.zoomX,
    startZoomY: currentZoom.zoomY,
    startPointer: point,
    startWidth: model.adapter.geometry.width,
    startHeight: model.adapter.geometry.height,
  }
}

function resetDraftState() {
  dragState.value = null
  hasPendingImageEventChange = false
  draftImagePatch.value = null
  draftChoicePositionMap.value = null
  draftCustomUiPositionMap.value = null
  guideCoordinate.value = null
  guideLabel.value = ''
}

function releasePointer(pointerId: number) {
  const captureEl = hostRef.value ?? rootRef.value
  if (captureEl?.hasPointerCapture(pointerId)) {
    captureEl.releasePointerCapture(pointerId)
  }
  flushPendingCommit()
  commitPendingImageEventChange()
  resetDraftState()
}

function onPointerDown(event: PointerEvent) {
  const point = getPointerPoint(event)
  const model = hitRenderModel(point)
  if (!model) return
  const isPivotHandle = model.adapter.capabilities.canEditPivot
    && Math.hypot(point.x - model.pivotScreen.x, point.y - model.pivotScreen.y) <= 12
  const isBody = isPointInsidePreviewQuad(point, model.quad)
  if (!isPivotHandle && !isBody) return
  event.preventDefault()
  event.stopPropagation()
  hostRef.value?.setPointerCapture(event.pointerId)
  activeAdapterId.value = model.adapter.id
  model.adapter.onSelect?.()
  if (isPivotHandle) {
    dragState.value = {
      pointerId: event.pointerId,
      adapterId: model.adapter.id,
      mode: 'pivot',
    }
    model.adapter.applyPivot(point, model.adapter.geometry)
    return
  }
  if (isBody && model.adapter.capabilities.canRotate) {
    guideCoordinate.value = getGuideAnchor(model)
    guideLabel.value = formatAngleLabel(model.adapter.geometry.rotation)
    dragState.value = {
      pointerId: event.pointerId,
      adapterId: model.adapter.id,
      mode: 'rotate',
      startPointerY: point.y,
      startRotation: model.adapter.geometry.rotation,
    }
    return
  }
  if (isBody && model.adapter.capabilities.canMove) {
    const designPoint = toDesignPoint(point, props.previewScale)
    guideCoordinate.value = {
      x: model.adapter.geometry.x,
      y: model.adapter.geometry.y,
    }
    guideLabel.value = ''
    dragState.value = {
      pointerId: event.pointerId,
      adapterId: model.adapter.id,
      mode: 'move',
      startPointer: designPoint,
      startX: model.adapter.geometry.x,
      startY: model.adapter.geometry.y,
    }
  }
}

function onPointerMove(event: PointerEvent) {
  const point = getPointerPoint(event)
  const current = dragState.value
  if (!current) return
  event.preventDefault()
  event.stopPropagation()
  if (current.mode !== 'scale' && current.pointerId !== event.pointerId) return
  const model = getRenderModelById(current.adapterId)
  if (!model) return
  if (current.mode === 'move') {
    const designPoint = toDesignPoint(point, props.previewScale)
    const nextX = snapMoveValue(current.startX + (designPoint.x - current.startPointer.x))
    const nextY = snapMoveValue(current.startY + (designPoint.y - current.startPointer.y))
    guideCoordinate.value = { x: nextX, y: nextY }
    model.adapter.applyMove(nextX, nextY)
    return
  }
  if (current.mode === 'scale') {
    const dx = toDesignPoint(point, props.previewScale).x - toDesignPoint(current.startPointer, props.previewScale).x
    const nextWidth = Math.max(1, current.startWidth + dx)
    const snapped = resolveSnappedScale(current, nextWidth / Math.max(1, current.startWidth))
    const nextZoomX = snapped.zoomX
    const nextZoomY = snapped.zoomY
    updateScaleGuide(model, snapped.width, snapped.height)
    model.adapter.applyScale(nextZoomX, nextZoomY)
    return
  }
  if (current.mode === 'pivot') {
    model.adapter.applyPivot(point, model.adapter.geometry)
    return
  }
  const nextRotation = snapRotationValue(current.startRotation - (current.startPointerY - point.y) * 0.4)
  guideCoordinate.value = getGuideAnchor(model)
  guideLabel.value = formatAngleLabel(nextRotation)
  model.adapter.applyRotate(current.startPointerY - point.y, current.startRotation)
}

function onPointerUp(event: PointerEvent) {
  releasePointer(event.pointerId)
}

function onPointerCancel(event: PointerEvent) {
  releasePointer(event.pointerId)
}

function onScaleHandlePointerDown(model: RenderTransformModel, event: PointerEvent) {
  if (!model.adapter.capabilities.canScale) return
  event.preventDefault()
  event.stopPropagation()
  const point = getPointerPoint(event)
  hostRef.value?.setPointerCapture(event.pointerId)
  activeAdapterId.value = model.adapter.id
  beginPointerScale(model, event.pointerId, point)
}

watch(
  () => props.selectedIndex,
  () => {
    flushPendingCommit()
    commitPendingImageEventChange()
    resetDraftState()
    activeAdapterId.value = null
  },
)

onBeforeUnmount(() => {
  flushPendingCommit()
  commitPendingImageEventChange()
})
</script>

<template>
  <div
    v-if="isVisible"
    ref="hostRef"
    class="canvas-edit-overlay-host"
    @pointermove="onPointerMove"
    @pointerup="onPointerUp"
    @pointercancel="onPointerCancel"
  >
    <svg
      ref="rootRef"
      class="canvas-edit-overlay"
      :width="stageWidth"
      :height="stageHeight"
      @pointerdown="onPointerDown"
    >
      <g
        v-for="model in renderModels"
        :key="model.adapter.id"
        :class="{ 'canvas-edit-overlay__choice--active': activeAdapterId === model.adapter.id }"
      >
        <polyline
          class="canvas-edit-overlay__line"
          :points="`${getImagePath(model)} ${model.quad[0].x},${model.quad[0].y}`"
        />
        <template v-if="model.adapter.capabilities.showPivot">
          <line
            class="canvas-edit-overlay__pivot-cross"
            :x1="model.pivotScreen.x - 10"
            :y1="model.pivotScreen.y"
            :x2="model.pivotScreen.x + 10"
            :y2="model.pivotScreen.y"
          />
          <line
            class="canvas-edit-overlay__pivot-cross"
            :x1="model.pivotScreen.x"
            :y1="model.pivotScreen.y - 10"
            :x2="model.pivotScreen.x"
            :y2="model.pivotScreen.y + 10"
          />
          <circle
            class="canvas-edit-overlay__pivot"
            :class="{ 'canvas-edit-overlay__pivot--editable': model.adapter.capabilities.canEditPivot }"
            :cx="model.pivotScreen.x"
            :cy="model.pivotScreen.y"
            r="8"
          />
        </template>
      </g>
    </svg>
    <button
      v-for="model in renderModels"
      v-show="model.adapter.capabilities.canScale"
      :key="`scale:${model.adapter.id}`"
      type="button"
      class="canvas-edit-overlay__scale-handle"
      :class="{ 'canvas-edit-overlay__scale-handle--active': activeAdapterId === model.adapter.id }"
      :style="{
        left: `${model.quad[2].x - 16}px`,
        top: `${model.quad[2].y - 16}px`,
      }"
      aria-label="缩放图片"
      @pointerdown="onScaleHandlePointerDown(model, $event)"
    />
    <PreviewGuideOverlay
      :visible="guideVisible"
      :design-width="project?.screenHash?.width || stageWidth"
      :design-height="project?.screenHash?.height || stageHeight"
      :surface-width="stageWidth"
      :surface-height="stageHeight"
      :stage-scale="guideStageScale"
      :coordinate="guideCoordinate"
      :label="guideLabel"
      :show-grid="true"
    />
  </div>
</template>

<style scoped>
.canvas-edit-overlay-host {
  position: absolute;
  inset: 0;
  z-index: 4;
  pointer-events: none;
  user-select: none;
}

.canvas-edit-overlay {
  position: absolute;
  inset: 0;
  z-index: 7;
  overflow: visible;
  touch-action: none;
  pointer-events: auto;
}

.canvas-edit-overlay__line {
  fill: none;
  stroke: #60a5fa;
  stroke-width: 2;
  vector-effect: non-scaling-stroke;
}

.canvas-edit-overlay__scale-handle {
  position: absolute;
  z-index: 8;
  width: 32px;
  height: 32px;
  margin: 0;
  padding: 0;
  border: 0;
  background: none;
  cursor: nwse-resize;
  pointer-events: auto;
  touch-action: none;
}

.canvas-edit-overlay__scale-handle::after {
  content: '';
  position: absolute;
  right: 4px;
  bottom: 4px;
  width: 12px;
  height: 12px;
  border-right: 2px solid #bfdbfe;
  border-bottom: 2px solid #bfdbfe;
  box-sizing: border-box;
}

.canvas-edit-overlay__scale-handle--active::after {
  border-right-color: #fbbf24;
  border-bottom-color: #fbbf24;
}

.canvas-edit-overlay__pivot {
  fill: #334155;
  stroke: #94a3b8;
  stroke-width: 2;
}

.canvas-edit-overlay__pivot--editable {
  fill: #10b981;
  stroke: #d1fae5;
}

.canvas-edit-overlay__pivot-cross {
  stroke: #f8fafc;
  stroke-width: 1.5;
  vector-effect: non-scaling-stroke;
}

.canvas-edit-overlay__choice--active .canvas-edit-overlay__line {
  fill: none;
  stroke: #fbbf24;
}
</style>
